import { NotImplementedError } from "../../../../errors";
import { parseBidstreamResponse } from "../../../../models/looker/modules/bidstream";
import { FilterTypes } from "../../../../types";
import { PromiseHandler } from "../../../../handlers";
import { defaultDateRange, formatDateRange, priorPeriodDateRange } from "../shared/dateRange";
import DealIdOptions from "./dealIds";
import { BidstreamFetchFailed } from "./errors";
import { BIDSTREAM_QUERY } from "./query";
import RejectionReasons from "./rejectionReasons";
import BidstreamTrend from "./trend";
class Bidstream extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "looker-bidstream");
        /**
         * See ./rejectionReasons/index.ts for the query shape. Exposed
         * directly (rather than `.rejectionReasons.find_once(...)`) so
         * callers just do `bidstream.rejectionReasons({ where: [...] })`.
         */
        this.rejectionReasons = (filters) => this.rejectionReasonsHandler.find_once(filters);
        /**
         * With `dates`: that window's Deal ID list. Without: the full history
         * check. See dealIds.ts. `dates` comes last, after the existing
         * `forceRefresh`, so an old 2-arg call (`dealIdOptions(id, true)`) keeps
         * working. Public for the same reason as rejectionReasons above.
         */
        this.dealIdOptions = (lineItemId, forceRefresh = false, dates) => this.dealIdOptionsHandler.find_once({
            where: [
                { field: "lineItemId", type: FilterTypes.EQ, value: lineItemId },
                ...(dates ? [{ field: "dates", type: FilterTypes.EQ, value: dates }] : []),
                { field: "forceRefresh", type: FilterTypes.EQ, value: forceRefresh }
            ]
        });
        /**
         * See ./trend.ts for the query shape and why it's a separate
         * PromiseHandler from findItems below (a different response shape
         * and cache key than the summary query). Exposed directly, same
         * reasoning as rejectionReasons/dealIdOptions above.
         */
        this.trend = (filters) => this.trendHandler.find_once(filters);
        this.rejectionReasonsHandler = new RejectionReasons(sdk);
        this.dealIdOptionsHandler = new DealIdOptions(sdk);
        this.trendHandler = new BidstreamTrend(sdk);
    }
    async findItems(filters) {
        const lineItemId = filters.where?.find((filter) => filter.field === "lineItemId")?.value;
        const datesFilter = filters.where?.find((filter) => filter.field === "dates")?.value;
        const dealIdsFilter = filters.where?.find((filter) => filter.field === "dealIds")?.value;
        const forceRefresh = filters.where?.find((filter) => filter.field === "forceRefresh")?.value === true;
        if (typeof lineItemId !== "string" || !lineItemId) {
            throw new Error("lineItemId filter is required");
        }
        const dates = Array.isArray(datesFilter) &&
            datesFilter.length === 2 &&
            datesFilter.every((date) => date instanceof Date)
            ? datesFilter
            : defaultDateRange();
        const dealIds = Array.isArray(dealIdsFilter)
            ? dealIdsFilter.filter((id) => typeof id === "string")
            : [];
        const buildQuery = (dateRange) => ({
            ...BIDSTREAM_QUERY,
            filters: {
                ...BIDSTREAM_QUERY.filters,
                "explore_bidstream_base.flt_lineitem_id": lineItemId,
                "explore_bidstream_base.flt_analysis_interval": formatDateRange(dateRange),
                // Looker string filters treat a comma-separated value as an OR list.
                ...(dealIds.length > 0 && {
                    "explore_bidstream_base.flt_deal_id": dealIds.join(",")
                })
            }
        });
        const queryBody = buildQuery(dates);
        const priorQueryBody = buildQuery(priorPeriodDateRange(dates));
        const hashKey = await this.sdk.looker.query.hashQuery(queryBody);
        // this.cache.promise below is a plain cache-or-fetch: an identical query
        // (same lineItemId/date-range/dealIds) hit within the cache's ~83min TTL
        // returns the stale cached result. That's the desired behavior for a
        // filter change (a new filter combination is a new hashKey anyway, so it
        // always fetches) or navigating back to a recently-viewed one (cheap,
        // instant). It's the wrong behavior for the drawer's explicit Refresh
        // button, which promises "get current data" - so refetch() (see
        // useBidstreamData.ts) passes forceRefresh: true, which evicts this
        // exact query's cache entry first, guaranteeing the promise below is a
        // real network call rather than a replay.
        if (forceRefresh) {
            this.cache.remove(hashKey);
        }
        // No built-in comparison measure, so prior-period deltas come from a
        // second query, diffed client-side in parseBidstreamResponse.
        return this.cache.promise(hashKey, () => Promise.all([this.sdk.looker.query.run(queryBody), this.sdk.looker.query.run(priorQueryBody)])
            .then(([current, prior]) => [
            {
                data: parseBidstreamResponse(current.data, prior.data),
                queryMeta: current.queryMeta
            }
        ])
            .catch((error) => {
            throw new BidstreamFetchFailed(error);
        }));
    }
    async saveItem() {
        throw new NotImplementedError("saveItem");
    }
    async deleteItem() {
        throw new NotImplementedError("deleteItem");
    }
}
export default Bidstream;
//# sourceMappingURL=index.js.map